import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../../store/authStore'
import { supabase } from '../supabase'
import type { PermissionLevel } from './pageRegistry'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

export type PagePermissionsMap = Map<string, PermissionLevel>

type RolePagePermissionRow = {
  page_key: string
  level: PermissionLevel
}

/**
 * Owner role bypass: returns 'edit' for every queried key.
 * Owner is identified at the user level via tenant_users.role = 'owner',
 * stored in the auth store as `role`.
 */
function isOwnerRole(role: string | null): boolean {
  return role === 'owner'
}

/**
 * Page-level permission map for the current user.
 *
 *  - Owner (and platform `super_admin`) → all keys resolve to 'edit'.
 *  - Other roles → look up role_page_permissions for the user's role_id.
 *  - Missing rows default to 'none'.
 *
 * Use `getLevel(key)` for ergonomic reads. Consumers should treat the
 * isLoading flag as "permission unknown" — render skeletons or hold UI
 * until the query resolves rather than flashing forbidden pages.
 */
export function useCurrentUserPagePermissions() {
  const { tenant, user, role } = useAuthStore()
  const tid = tenant?.id ?? ''
  const uid = user?.id ?? ''
  const isSuperAdmin = role === 'super_admin'
  const isOwner = isOwnerRole(role)
  // Both the tenant owner and the platform super_admin skip the per-role page
  // lookup (every key resolves to 'edit'). They differ for PLAN enforcement:
  // super_admin bypasses plan too, the owner does not.
  const owner = isOwner || isSuperAdmin

  const { data: roleId } = useQuery<string | null>({
    queryKey: ['current-user-role-id', tid, uid],
    enabled: !!tid && !!uid && !owner,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await db('tenant_users')
        .select('role_id')
        .eq('tenant_id', tid)
        .eq('user_id', uid)
        .maybeSingle()
      return (data?.role_id as string | null) ?? null
    },
  })

  const { data: rows = [], isLoading } = useQuery<RolePagePermissionRow[]>({
    queryKey: ['role-page-permissions', tid, roleId],
    enabled: !!tid && !!roleId && !owner,
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data, error } = await db('role_page_permissions')
        .select('page_key, level')
        .eq('tenant_id', tid)
        .eq('role_id', roleId)
      if (error) throw error
      return (data ?? []) as RolePagePermissionRow[]
    },
  })

  const map: PagePermissionsMap = new Map()
  if (!owner) {
    for (const r of rows) map.set(r.page_key, r.level)
  }

  function getLevel(pageKey: string): PermissionLevel {
    if (owner) return 'edit'
    return map.get(pageKey) ?? 'none'
  }

  return {
    isOwner,
    isSuperAdmin,
    isLoading: owner ? false : (isLoading && !!roleId),
    levels: map,
    getLevel,
  }
}

/**
 * Permissions for a specific role (used in the role-detail edit screen).
 * Returns the raw map of page_key → level for that role only. Pages absent
 * from the table default to 'none' at read time.
 */
export function useRolePagePermissions(roleId: string | null) {
  const { tenant } = useAuthStore()
  const tid = tenant?.id ?? ''

  return useQuery<PagePermissionsMap>({
    queryKey: ['role-page-permissions-for-role', tid, roleId],
    enabled: !!tid && !!roleId,
    staleTime: 30 * 1000,
    queryFn: async () => {
      const { data, error } = await db('role_page_permissions')
        .select('page_key, level')
        .eq('tenant_id', tid)
        .eq('role_id', roleId)
      if (error) throw error
      const map: PagePermissionsMap = new Map()
      for (const r of (data ?? []) as RolePagePermissionRow[]) {
        map.set(r.page_key, r.level)
      }
      return map
    },
  })
}
